import { isEmpty, size } from 'lodash';
import { FormikValues } from 'formik';
import { Tooltip } from 'react-tooltip';
import { useSelector } from 'react-redux';

import Button from 'shared/components/form/button';
import { notify } from 'shared/components/notification/notification';
import { QuestionMarkIcon } from 'shared/icons/icon';
import { ChartAxisPosition, ListType, ScaleType } from 'shared/interface/enums';
import i18n from 'shared/util/localization';
import localizationConstants from 'shared/util/translation/constants';
import { Translate } from 'shared/util/translation/translate';
import { State } from 'shared/interface';

interface IAdvanceSettingsPopupFooter {
	setFieldValue: (name: string, value: any) => void;
	dirty: boolean;
	setAction: (action: string) => void;
	handleSubmit: () => void;
	values: FormikValues;
	updateChartViewLoading: boolean;
	handleCurrencyList: (currencyType: string, setFieldValue?: (field: string, value: any) => void) => void;
}

const radioOptions = [
	{
		value: ListType.DEFAULT,
		label: localizationConstants.forex,
		tooltipId: 'defaultStandardRadioTooltip',
		tooltipMsg: localizationConstants.currencyDefaultTooltipMessage,
		hasTooltip: false
	},
	{ value: ListType.LME, label: localizationConstants.lme },
	{
		value: ListType.BLOOMBERG,
		label: localizationConstants.bloomberg,
		tooltipId: 'defaultBloombergRadioTooltip',
		tooltipMsg: localizationConstants.currencyBloombergTooltipMessage,
		hasTooltip: true
	},
	{ value: ListType.ECB, label: localizationConstants.ecb }
];

const scaleOptions = [
	{
		value: ScaleType.PRICE_AXIS,
		label: localizationConstants.default,
		axis: ChartAxisPosition.RIGHT_AXIS,
		tooltipId: 'defaultRadioTooltip',
		tooltipMsg: localizationConstants.defaultTooltipMessage
	},
	{
		value: ScaleType.PERCENTAGE_CHANGE,
		label: localizationConstants.percentageChangeSingle,
		axis: ChartAxisPosition.LEFT_AXIS,
		tooltipId: 'changeRadioTooltip',
		tooltipMsg: localizationConstants.changeToolTipMessage
	},
	{
		value: ScaleType.PERCENTAGE_CHANGE_TOTAL,
		label: localizationConstants.percentageChangeTotal,
		axis: ChartAxisPosition.LEFT_AXIS,
		tooltipId: 'changeRadioTooltip',
		tooltipMsg: localizationConstants.changeToolTipMessage
	},
	{
		value: ScaleType.LOGARITHAMIC_AXIS,
		label: localizationConstants.logarithmicYAxis,
		axis: ChartAxisPosition.RIGHT_AXIS,
		tooltipId: 'logarithmToolTipMessage',
		tooltipMsg: localizationConstants.LogarithmicYAxisTooltipMessage
	}
];

const AdvanceSettingsPopupFooter: React.FC<IAdvanceSettingsPopupFooter> = ({
	setFieldValue,
	dirty,
	values,
	handleSubmit,
	setAction,
	updateChartViewLoading,
	handleCurrencyList
}) => {
	const { t } = i18n;
	const { language } = useSelector((state: State) => state.siteConfig);
	const validate = values.chartSettings.filter((item: any) => item.history.value !== '');

	const handleCurrencyChange = (value: string) => {
		setFieldValue('currencyType', value);
		handleCurrencyList(value, setFieldValue);
	};

	const handleScaleChange = (value: string, axis: ChartAxisPosition) => {
		setFieldValue('scaleType', value);
		values.chartSettings.forEach((_: any, index: number) => {
			setFieldValue(`chartSettings[${index}].axis`, {
				value: axis,
				label: `${t(axis === ChartAxisPosition.RIGHT_AXIS ? localizationConstants.right : localizationConstants.left)}`
			});
		});
	};

	const renderCurrencyRadio = () =>
		radioOptions.map(({ value, label, tooltipId, tooltipMsg, hasTooltip }) => (
			<div key={value} className='radio-input-wrapper'>
				<input
					id={`currencyType${value}`}
					type='radio'
					name='currencyType'
					value={value}
					checked={value === values.currencyType}
					onChange={() => handleCurrencyChange(value)}
				/>
				<label
					htmlFor={`currencyType${value}`}
					className={`pointer mr-26 text--secondary ${value === values.currencyType ? 'checked' : ''} font-weight--400 text--dark-blue`}
				>
					<Translate text={label} />
					{hasTooltip && (
						<>
							<QuestionMarkIcon className='position--absolute ml-5' id={tooltipId} />
							<Tooltip
								anchorId={tooltipId}
								place='top'
								className='no-padding duplicate-chart-tooltip'
								content={t(tooltipMsg) as string}
							/>
						</>
					)}
				</label>
			</div>
		));

	const renderScaleRadio = () =>
		scaleOptions.map(({ value, label, axis, tooltipId, tooltipMsg }) => (
			<div key={value} className='radio-input-wrapper'>
				<input
					id={`scaleType${value}`}
					type='radio'
					name='scaleType'
					value={value}
					checked={value === values.scaleType}
					onChange={() => handleScaleChange(value, axis)}
				/>
				<label
					htmlFor={`scaleType${value}`}
					className={`pointer mr-26 text--secondary ${value === values.scaleType ? 'checked' : ''} font-weight--400 text--dark-blue`}
				>
					<Translate text={label} />
					<QuestionMarkIcon className='position--absolute ml-5' id={tooltipId} />
					<Tooltip anchorId={tooltipId} place='top' className='no-padding duplicate-chart-tooltip' content={t(tooltipMsg) as string} />
				</label>
			</div>
		));

	return (
		<div className='advance-settings-footer pt--36'>
			<h2 className='font-weight--700 line-height--20px font--15px no-margin mb-20'>
				<Translate text={localizationConstants.advancedSettings} />
			</h2>

			<div>
				<div className='d-flex ml-80'>
					<h2 className='font-weight--700 line-height--20px font--15px no-margin'>
						<Translate text={localizationConstants.currencyConversion} />
					</h2>
					<div className={`ml-${language === 'de' ? '45' : '96'} d-flex`}>{renderCurrencyRadio()}</div>
				</div>

				<div className='d-flex justify-content-space-between align-items-center ml-80'>
					<h2 className='font-weight--700 line-height--20px font--15px no-margin'>
						<Translate text={localizationConstants.chartView} />
					</h2>
					<div className='checkbox-container d-flex ml-36'>{renderScaleRadio()}</div>
					<div className='btn-wrapper d-flex'>
						<Button
							btnType='secondary'
							type='button'
							onClick={() => setAction('')}
							className='d-flex button button-size--medium justify-content-center align-items-center br--6px p--6 no-border'
						>
							<Translate text={localizationConstants.cancel} />
						</Button>
						<Button
							btnType='primary'
							type='button'
							disabled={!dirty}
							loading={updateChartViewLoading}
							onClick={() =>
								!isEmpty(validate) && size(validate) !== size(values.chartSettings)
									? notify(t(localizationConstants.pleaseSelectAllHistoryDropdown))
									: handleSubmit()
							}
							className='d-flex button button-size--medium justify-content-center align-items-center br--6px p--6'
						>
							<Translate text={localizationConstants.submit} />
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AdvanceSettingsPopupFooter;
